<template>
    <div v-if="props.items && props.items.length"
        class="items-list"
        ref="containerRef"
        @mousedown.prevent="onContainerMouseDown"
        @click="onContainerClick"
    >
        <ul class="items"
            ref="ulRef"
            :style="ulStyle"
        >
            <li v-for="(item, idx) in props.items"
                :key="item.id"
                :data-key="item.id"
                class="explorer-item"
                :class="[props.itemClass, {
                    selected: data.selectedMap[item.id],
                    pending: data.pendingMap[item.id]
                }]"
                :style="itemStyle"

                @click.stop.prevent="onItemClick($event, item)"
                @dblclick.stop.prevent="onItemDblClick(item)"
                @contextmenu.stop.prevent="onItemContextMenu($event, item)"
            >
                <slot :item="item"
                    :selected="data.selectedMap[item.id]"
                    :inRect="data.pendingMap[item.id]"
                />
            </li>
        </ul>

        <div
            v-if="data.selectRect.visible"
            class="selection-rect"
            :style="{
                left: data.selectRect.x + 'px',
                top: data.selectRect.y + 'px',
                width: data.selectRect.width + 'px',
                height: data.selectRect.height + 'px'
            }"
        ></div>
    </div>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue';

const props = defineProps({
    items: { type: Array, default: () => [] },
    selectedItems: { type: Array, default: () => [] },

    colCount: { type: [Number, null], default: null },
    colGap: { type: String, default: '4px' },
    rowGap: { type: String, default: '4px' },

    itemClass: { type: [String, null], default: null },
    itemWidth: { type: [String, null], default: null },
    minItemWidth: { type: [String, null], default: null },
    maxItemWidth: { type: [String, null], default: null },
    itemHeight: { type: [String, null], default: null },

    maxSelectedCount: { type: [Number, null], default: null },

    selectViaClick: { type: Boolean, default: true },
    selectViaSelectArea: { type: Boolean, default: true },

    clearSelectionOnClick: { type: Boolean, default: true },
});

const emit = defineEmits(['select', 'dblclick']);

const data = reactive({
    keyToItem: {},

    selectedMap: {},
    selectedOrder: [],

    pendingMap: {},

    anchorId: null,

    suppressNextContainerClick: false,

    history: [],
    HISTORY_LIMIT: 50,

    selectRect: {
        visible: false,
        x: 0,
        y: 0,
        width: 0,
        height: 0,
        startX: 0,
        startY: 0,
    },

    onMouseMoveHandler: null,
    onMouseUpHandler: null,
});

const containerRef = ref(null);
const ulRef = ref(null);

const rebuildKeyMap = () => {
    const map = {};
    (props.items || []).forEach((item, index) => {
        map[item.id] = { item, index };
    });

    data.keyToItem = map;
}

const initSelectionFromProp = () => {
    data.selectedMap = {};
    data.selectedOrder = [];

    const arr = props.selectedItems ?? [];

    arr.forEach((item) => {
        if (item === null || item === undefined) {
            return;
        }

        if (typeof item === 'string') {
            if (data.keyToItem[item]) {
                data.selectedMap[item] = true;
                data.selectedOrder.push(item);
            }
        } else if (typeof item === 'object') {
            if (data.keyToItem[item.id]) {
                data.selectedMap[item.id] = true;
                data.selectedOrder.push(item.id);
            }
        }
    });
}

watch(
    () => props.items,
    () => {
        rebuildKeyMap();

        const validKeys = new Set(Object.keys(data.keyToItem));

        let changed = false;
        for (let i = data.selectedOrder.length - 1; i >= 0; i--) {
            const k = data.selectedOrder[i];

            if (!validKeys.has(k)) {
                delete data.selectedMap[k];
                data.selectedOrder.splice(i, 1);
                changed = true;
            }
        }

        if (changed) {
            emitSelectionChange();
        }
    }, {
        deep: true
    }
);

watch(
    () => props.selectedItems,
    () => {
        initSelectionFromProp();
        emitSelectionChange();
    }, {
        deep: true
    }
);

const pushHistory = () => {
    data.history.push([...data.selectedOrder]);
    if (data.history.length > data.HISTORY_LIMIT) {
        data.history.shift();
    }
}

const addIdToSelection = (id) => {
    if (!data.selectedMap[id]) {
        pushHistory();

        data.selectedMap[id] = true;
        data.selectedOrder.push(id);

        enforceMaxSelected();
        emitSelectionChange();
    }
}

const removeIdFromSelection = (id) => {
    if (data.selectedMap[id]) {
        pushHistory();

        delete data.selectedMap[id];

        const idx = data.selectedOrder.indexOf(id);
        if (idx !== -1) {
            data.selectedOrder.splice(idx, 1);
        }

        emitSelectionChange();
    }
}

const replaceSelectionWithIds = (ids) => {
    pushHistory();

    data.selectedMap = {};
    data.selectedOrder = [];

    ids.forEach(id => {
        if (data.keyToItem[id]) {
            data.selectedMap[id] = true;
            data.selectedOrder.push(id);
        }
    });

    enforceMaxSelected();
    emitSelectionChange();
}

const clearSelection = () => {
    if (data.selectedOrder.length === 0) {
        return;
    }

    pushHistory();

    data.selectedMap = {};
    data.selectedOrder = [];

    emitSelectionChange();
}

const enforceMaxSelected = () => {
    const max = props.maxSelectedCount;
    if (max != null && Number.isFinite(max)) {
        while (data.selectedOrder.length > max) {
            const oldest = data.selectedOrder.shift();
            if (oldest) {
                delete data.selectedMap[oldest];
            }
        }
    }
}

const undoLastChange = () => {
    if (!data.history.length) {
        return false;
    }

    const prev = data.history.pop();

    data.selectedMap = {};
    data.selectedOrder = [...prev];

    prev.forEach(id => {
        data.selectedMap[id] = true;
    });

    emitSelectionChange();
    return true;
}

const getSelectedItems = () => {
    return data.selectedOrder.map(id => data.keyToItem[id]?.item).filter(Boolean);
}

defineExpose({
    undoLastChange,
    getSelectedItems,
    clearSelection
});

const emitSelectionChange = () => {
    emit('select', getSelectedItems());
}

const onItemClick = (event, item) => {
    if (!props.selectViaClick) {
        return;
    }

    const ctrl = event.ctrlKey || event.metaKey;
    const shift = event.shiftKey;

    // SHIFT + Click — выделение диапазона
    if (shift && data.anchorId) {
        const anchorIndex = data.keyToItem[data.anchorId]?.index;
        const currentIndex = data.keyToItem[item.id]?.index;

        if (anchorIndex != null && currentIndex != null) {
            const [from, to] = anchorIndex < currentIndex
                ? [anchorIndex, currentIndex]
                : [currentIndex, anchorIndex];

            const idsInRange = [];
            for (let i = from; i <= to; i++) {
                const id = props.items[i].id;
                idsInRange.push(id);
            }

            replaceSelectionWithIds(idsInRange);

            // Обновляем anchor на последний кликнутый
            data.anchorId = item.id;
            return;
        }
    }

    // CTRL + Click — toggle
    if (ctrl || !props.clearSelectionOnClick) {
        if (data.selectedMap[item.id]) {
            removeIdFromSelection(item.id);
        } else {
            addIdToSelection(item.id);
        }

        // anchor всегда обновляется на последний клик
        data.anchorId = item.id;
        return;
    }

    // Обычный клик
    if (props.clearSelectionOnClick) {
        if (data.selectedOrder.length === 1 && data.selectedOrder[0] === item.id) {
            return;
        }

        replaceSelectionWithIds([item.id]);
    }

    // anchor обновляем
    data.anchorId = item.id;
}


const onItemDblClick = (item) => {
    emit('dblclick', item);
}

const onItemContextMenu = (event, item) => {
    if (data.selectedOrder.length <= 1) {
        replaceSelectionWithIds([item.id]);
        data.anchorId = item.id;
    }
    // дальше можно отдать событие наружу, если нужно
    // emit('contextmenu', item);  <-- если планируется своё меню
};

const onContainerClick = (event) => {
    // если недавно завершили селекцию — игнорируем один click
    if (data.suppressNextContainerClick) {
        data.suppressNextContainerClick = false;
        return;
    }

    const isBackground = event.target === containerRef.value || event.target === ulRef.value;

    if (isBackground && props.selectViaSelectArea) {
        const ctrl = event.ctrlKey || event.metaKey;

        if (!ctrl) {
            clearSelection();
        }
    }
}

// Drag
const startSelectionFromEvent = (e) => {
    // только левая кнопка
    if (e.button != null && e.button !== 0) return;
    if (!props.selectViaSelectArea) return;

    const container = containerRef.value;
    if (!container) return;

    const containerRect = container.getBoundingClientRect();

    const DRAG_START_THRESHOLD = 4;

    const startX = e.clientX;
    const startY = e.clientY;

    const isOnItem = !!(e.target?.closest && e.target.closest('.explorer-item'));

    data.selectRect.startX = startX;
    data.selectRect.startY = startY;

    data.selectRect.x = startX - containerRect.left;
    data.selectRect.y = startY - containerRect.top;
    data.selectRect.width = 0;
    data.selectRect.height = 0;

    data.selectRect.visible = !isOnItem;

    let selectionActivated = data.selectRect.visible;

    // helper: обновить pendingMap по текущему прямоугольнику (в координатах контейнера)
    const updatePendingFromRect = () => {
        const rectEl = {
            left: data.selectRect.x,
            top: data.selectRect.y,
            right: data.selectRect.x + data.selectRect.width,
            bottom: data.selectRect.y + data.selectRect.height
        };

        const containerRect2 = container.getBoundingClientRect();
        const selRectViewport = {
            left: containerRect2.left + rectEl.left,
            top: containerRect2.top + rectEl.top,
            right: containerRect2.left + rectEl.right,
            bottom: containerRect2.top + rectEl.bottom
        };

        const liElements = ulRef.value ? Array.from(ulRef.value.querySelectorAll('li')) : [];
        const newPending = {};

        liElements.forEach((li) => {
            const liRect = li.getBoundingClientRect();
            // пересечение прямоугольников
            const intersect = !(liRect.right < selRectViewport.left ||
                                liRect.left > selRectViewport.right ||
                                liRect.bottom < selRectViewport.top ||
                                liRect.top > selRectViewport.bottom);
            if (intersect) {
                const k = li.getAttribute('data-key');
                if (k) newPending[k] = true;
            }
        });

        // заменяем pendingMap целиком (реактивно)
        data.pendingMap = newPending;
    };

    // move handler
    data.onMouseMoveHandler = (ev) => {
        const sx = data.selectRect.startX;
        const sy = data.selectRect.startY;
        const ex = ev.clientX;
        const ey = ev.clientY;

        const dx = Math.abs(ex - sx);
        const dy = Math.abs(ey - sy);

        if (!selectionActivated && isOnItem) {
            if (dx < DRAG_START_THRESHOLD && dy < DRAG_START_THRESHOLD) {
                return; // ждать дальше
            }
            selectionActivated = true;
            data.selectRect.visible = true;
        }

        if (!selectionActivated) return;

        const left = Math.min(sx, ex);
        const top = Math.min(sy, ey);
        const width = Math.abs(ex - sx);
        const height = Math.abs(ey - sy);
        const containerRect2 = container.getBoundingClientRect();
        data.selectRect.x = left - containerRect2.left;
        data.selectRect.y = top - containerRect2.top;
        data.selectRect.width = width;
        data.selectRect.height = height;

        // обновляем pending по текущему прямоугольнику
        updatePendingFromRect();
    };

    // up handler
    data.onMouseUpHandler = (ev) => {
        if (!selectionActivated) {
            // клик без движения — очищаем pending (на всякий) и позволяем локальному click обработчику работать
            data.pendingMap = {};
            data.selectRect.visible = false;
        } else {
            // завершаем селекцию: применяем итоговое выделение и очищаем pending
            finishRectangleSelection(ev);
            data.pendingMap = {};
            data.selectRect.visible = false;

            // Важно: следующий click на контейнере — часть этой операции селекции.
            // Подавляем его, чтобы onContainerClick не сбросил только что установленную выборку.
            data.suppressNextContainerClick = true;
        }

        if (data.onMouseMoveHandler) {
            window.removeEventListener('mousemove', data.onMouseMoveHandler);
        }
        if (data.onMouseUpHandler) {
            window.removeEventListener('mouseup', data.onMouseUpHandler);
        }

        data.onMouseMoveHandler = null;
        data.onMouseUpHandler = null;
    };

    window.addEventListener('mousemove', data.onMouseMoveHandler);
    window.addEventListener('mouseup', data.onMouseUpHandler);
}

const onContainerMouseDown = (e) => {
    if (checkClosestBlockers(e)) {
        return;
    }

    startSelectionFromEvent(e);
}

const checkClosestBlockers = (e) => {
    const selectors = [
        '.explorer-ignore',
        '#modals-box',
        '.modal-box',
        '.modal-box-background',
        '.audio-toggle',
        '.footer',
        '.items-editor'
    ];

    if (selectors.some(selector => e.target?.closest  && e.target?.closest(selector))) {
        if (!e.target.closest('.include-explorer') || e.target.closest('.explorer-ignore')) {
            return true;
        }
    }
}

const onWindowMouseDown = (e) => {
    if (checkClosestBlockers(e)) {
        return;
    }

    const container = containerRef.value;

    // если клик внутри контейнера — не стартуем селекцию здесь,
    // локальный onContainerMouseDown / onItemClick обработают поведение.
    if (container && container.contains(e.target)) {
        // особенно: если клик по элементу — явно игнорируем
        if (e.target?.closest && e.target.closest('.explorer-item')) {
            return;
        }
        // если клик по фоновой области контейнера — тоже не стартуем здесь,
        // чтобы избежать гонки с локальным handler'ом, который висит на контейнере.
        return;
    }

    // если клик снаружи контейнера — стартуем селекцию (как требовалось)
    startSelectionFromEvent(e);
}

const finishRectangleSelection = (e) => {
    const rectsIntersect = (a, b) => {
        return !(a.right < b.left || a.left > b.right || a.bottom < b.top || a.top > b.bottom);
    }

    const rectEl = {
        left: data.selectRect.x,
        top: data.selectRect.y,
        right: data.selectRect.x + data.selectRect.width,
        bottom: data.selectRect.y + data.selectRect.height
    };

    const containerRect = containerRef.value.getBoundingClientRect();

    const selRectViewport = {
        left: containerRect.left + rectEl.left,
        top: containerRect.top + rectEl.top,
        right: containerRect.left + rectEl.right,
        bottom: containerRect.top + rectEl.bottom
    };

    const liElements = ulRef.value ? Array.from(ulRef.value.querySelectorAll('li')) : [];
    const intersectingIds = [];

    liElements.forEach((li) => {
        const liRect = li.getBoundingClientRect();
        if (rectsIntersect(liRect, selRectViewport)) {
            const k = li.getAttribute('data-key');
            if (k) intersectingIds.push(k);
        }
    });

    const ctrl = e.ctrlKey || e.metaKey;

    if (!ctrl) {
        replaceSelectionWithIds(intersectingIds);
    } else {
        pushHistory();

        intersectingIds.forEach(k => {
            if (!data.selectedMap[k]) {
                data.selectedMap[k] = true;
                data.selectedOrder.push(k);
            }
        });

        enforceMaxSelected();
        emitSelectionChange();
    }

    // очистим pending на всякий случай
    data.pendingMap = {};
}


onMounted(() => {
    nextTick(() => {
        rebuildKeyMap();
        initSelectionFromProp();
        emitSelectionChange();
    });

    // вешаем глобальный mousedown, чтобы можно было начинать выделение за пределами контейнера
    window.addEventListener('mousedown', onWindowMouseDown, true);
});

onBeforeUnmount(() => {
    if (data.onMouseMoveHandler) {
        window.removeEventListener('mousemove', data.onMouseMoveHandler);
    }

    if (data.onMouseUpHandler) {
        window.removeEventListener('mouseup', data.onMouseUpHandler);
    }

    window.removeEventListener('mousedown', onWindowMouseDown, true);
});

// Styles
const ulStyle = computed(() => {
    const style = {
        display: 'flex',
        'flex-wrap': 'wrap',
        gap: `${props.rowGap || '0px'} ${props.colGap || '0px'}`,
        // 'align-items': 'flex-start',
    };

    if (props.colCount && !props.itemWidth) {
        style['--col-count'] = props.colCount;
    }

    return style;
});

const itemStyle = computed(() => {
    const st = {};

    if (props.itemWidth) {
        st.width = props.itemWidth;
        st['max-width'] = props.itemWidth;
        st['min-width'] = props.itemWidth;
    }

    if (props.itemHeight) {
        st.height = props.itemHeight;
    }

    if (props.colCount && !props.itemWidth) {
        const c = Number(props.colCount) || 1;
        st.flex = `0 0 calc(${100 / c}% - (${props.colGap} * (${c - 1}) / ${c}))`;

        st['max-width'] = `calc(${100 / c}% - (${props.colGap} * (${c - 1}) / ${c}))`;
    } else {
        st.flex = '0 0 auto';
    }

    if (props.minItemWidth) {
        st['min-width'] = props.minItemWidth;
    }

    if (props.maxItemWidth) {
        st['max-width'] = props.maxItemWidth;
    }


    return st;
});
</script>


<style scoped lang="scss">
.items-list {
    position: relative;
    user-select: none;
    padding: 4px;

    .items {
        list-style: none;
        margin: 0;
        padding: 0;
        align-items: stretch;

        & > li {
            box-sizing: border-box;
            padding: 6px;
            margin: 0;
            border-radius: 10px;
            cursor: pointer;
            transition: box-shadow .12s, background .12s;
            display: flex;
            // align-items: center;
            justify-content: center;

            &.selected {
                background: var(--primary-10);
            }
            &.pending {
                background: var(--primary-20);
            }
        }
    }
}

.selection-rect {
    position: absolute;
    pointer-events: none;
    border: 1px dashed var(--secondary-20);
    background: var(--secondary-10);
    z-index: 50;
    border-radius: 4px;
}
</style>
